// Invite a staff member (spec/07 §6.5, F-IAM-08): by phone or by email, naming one organisation role
// and the venues they may work at — by email only while phone sign-in is off on the Platform
// (`auth.phoneSignIn`, since 2026-10-08): no choice of channel, and no phone field. The invitee opens the
// link, signs in with the account that has that contact, and accepts (`/invitations/accept`). On success the dialog shows the link to pass on and
// whether the message went, so a front desk can hand it over when no message was sent.

import type { CreateInvitation, CreatedInvitation } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Checkbox, Dialog, RadioGroup, SegmentedControl, SelectField, TextField } from '@clavian/ui-web';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { useMyVenues } from '../../api/queries';
import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { usePhoneSignIn } from '../../auth/sign-in-methods';
import { Notice, Section } from '../../ui';
import { useScopeIds } from '../../shell/scope';
import { useCreateInvitation, useRoles } from './queries';

type Channel = 'phone' | 'email';

export function InviteDialog({ open, onOpenChange }: { readonly open: boolean; readonly onOpenChange: (open: boolean) => void }): ReactNode {
  const { t } = useTranslation('console');
  const phoneOn = usePhoneSignIn();
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      dirty="auto"
      eyebrow={t('staff.invite.eyebrow')}
      title={t('staff.invite.title')}
      description={t(phoneOn ? 'staff.invite.subtitle' : 'staff.invite.subtitleEmail')}
    >
      {open ? <InviteForm onClose={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

function InviteForm({ onClose }: { readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const scope = useScopeIds();
  const roles = useRoles(scope.organizationId);
  const venues = useMyVenues();
  const create = useCreateInvitation(scope.organizationId);

  const orgVenues = useMemo(() => (venues.data ?? []).filter((venue) => venue.organizationId === scope.organizationId), [venues.data, scope.organizationId]);

  // Phone invitations exist only while phone sign-in is on; until `GET /v1/meta` says so, email.
  const phoneOn = usePhoneSignIn();
  const [chosen, setChannel] = useState<Channel>('phone');
  const channel: Channel = phoneOn ? chosen : 'email';
  const [destination, setDestination] = useState('');
  const [role, setRole] = useState('');
  const [venueChoice, setVenueChoice] = useState<'all' | 'some'>('all');
  const [venueIds, setVenueIds] = useState<readonly string[]>([]);
  // Each problem sits on its own field; `form` is for a failure the whole form shares (the server refused it).
  const [errors, setErrors] = useState<{ destination?: string; role?: string; venues?: string; form?: string }>({});
  const [created, setCreated] = useState<CreatedInvitation | null>(null);

  // The role is an explicit choice — no default, so an owner is never granted by leaving the field.
  const roleOptions = (roles.data ?? []).map((candidate) => ({ value: candidate.name, label: candidate.name }));

  const destinationError = (): string | undefined =>
    destination.trim() === '' ? t(phoneOn ? 'staff.invite.destinationRequired' : 'staff.invite.emailRequired') : undefined;
  const roleError = (): string | undefined => (role === '' ? t('staff.invite.roleRequired') : undefined);
  const venuesError = (): string | undefined => (venueChoice === 'some' && venueIds.length === 0 ? t('staff.invite.venuesRequired') : undefined);
  const clear = (field: 'destination' | 'role' | 'venues') => setErrors((current) => (current[field] === undefined && current.form === undefined ? current : { ...current, [field]: undefined, form: undefined }));

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next = { destination: destinationError(), role: roleError(), venues: venuesError() };
    setErrors(next);
    if (next.destination !== undefined || next.role !== undefined || next.venues !== undefined) return;
    const contact = destination.trim();
    const body: CreateInvitation = {
      role,
      ...(channel === 'phone' ? { phoneNumber: contact } : { email: contact }),
      ...(venueChoice === 'some' ? { venueIds: [...venueIds] } : {}),
    };
    create.mutate(body, {
      onSuccess: (invitation) => setCreated(invitation),
      onError: (mutationError) => {
        const code = problemCode(mutationError);
        if (code === PROBLEM.alreadyAMember) setErrors({ destination: t('staff.invite.alreadyMember') });
        // Turned off since the form was drawn: said plainly, and the form goes back to email.
        else if (code === PROBLEM.phoneInvitationsOff) setErrors({ form: t('staff.invite.phoneOff') });
        else {
          const field = problem.fieldMessage(mutationError, 'email') ?? problem.fieldMessage(mutationError, 'phoneNumber');
          if (field !== undefined) setErrors({ destination: field });
          else setErrors({ form: problem.text(mutationError, { fallback: t('staff.invite.failed') }) });
        }
      },
    });
  }

  if (created !== null) {
    return (
      <div className="console-form">
        <Notice tone="ok" role="status" title={t('staff.invite.doneTitle')}>
          {created.delivery.status === 'sent' ? t('staff.invite.sent', { destination: created.destination }) : t('staff.invite.notSent', { destination: created.destination })}
        </Notice>
        <Section title={t('staff.invite.linkTitle')} tip={t('staff.invite.linkHint')}>
          <p className="console-invite-link">{created.acceptUrl}</p>
        </Section>
        <div className="console-form__actions">
          <Button variant="ghost" onClick={() => setCreated(null)}>
            {t('staff.invite.another')}
          </Button>
          <Button variant="primary" onClick={onClose}>
            {t('staff.invite.close')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form className="console-form" onSubmit={submit} noValidate>
      {errors.form !== undefined ? (
        <Notice tone="danger" role="alert">
          {errors.form}
        </Notice>
      ) : null}

      {phoneOn ? (
        <div className="console-people-field">
          <span className="clav-field__label">{t('staff.invite.channel')}</span>
          <SegmentedControl<Channel>
            label={t('staff.invite.channel')}
            value={channel}
            onValueChange={(next) => {
              setChannel(next);
              setDestination('');
            }}
            options={[
              { value: 'phone', label: t('staff.invite.phone') },
              { value: 'email', label: t('staff.invite.email') },
            ]}
          />
        </div>
      ) : null}

      <TextField
        label={channel === 'phone' ? t('staff.invite.phoneLabel') : t('staff.invite.emailLabel')}
        description={channel === 'phone' ? t('staff.invite.phoneHint') : t('staff.invite.emailHint')}
        type={channel === 'phone' ? 'tel' : 'email'}
        required
        value={destination}
        onChange={(event) => {
          setDestination(event.target.value);
          clear('destination');
        }}
        onBlur={() => setErrors((current) => ({ ...current, destination: destinationError() }))}
        autoComplete="off"
        error={errors.destination}
      />

      <SelectField
        label={t('staff.invite.roleLabel')}
        description={t('staff.invite.roleHint')}
        required
        placeholder={t('staff.invite.rolePlaceholder')}
        value={role}
        options={roleOptions}
        onValueChange={(next) => {
          setRole(next);
          clear('role');
        }}
        error={errors.role}
      />

      {/* One venue has nothing to choose: the invitation is for it. With more, the choice stays in view. */}
      {orgVenues.length > 1 ? (
      <RadioGroup
        label={t('staff.invite.venuesLabel')}
        value={venueChoice}
        onValueChange={(next) => {
          setVenueChoice(next as 'all' | 'some');
          clear('venues');
        }}
        options={[
          { value: 'all', label: t('staff.invite.everyVenue') },
          { value: 'some', label: t('staff.invite.chooseVenues') },
        ]}
      />
      ) : null}

      {orgVenues.length > 1 && venueChoice === 'some' ? (
        <fieldset className="console-venue-picker">
          <legend className="clav-field__label">{t('staff.invite.venuesPick')}</legend>
          {orgVenues.length === 0 ? (
            <p className="console-subtle">{t('staff.invite.noVenues')}</p>
          ) : (
            orgVenues.map((venue) => (
              <Checkbox
                key={venue.id}
                label={venue.name}
                checked={venueIds.includes(venue.id)}
                onCheckedChange={(checked) => {
                  setVenueIds((current) => (checked === true ? [...current, venue.id] : current.filter((id) => id !== venue.id)));
                  clear('venues');
                }}
              />
            ))
          )}
          {errors.venues !== undefined ? <p className="clav-field__error">{errors.venues}</p> : null}
        </fieldset>
      ) : null}

      <Button type="submit" variant="primary" size="lg" block busy={create.isPending}>
        {t('staff.invite.send')}
      </Button>
    </form>
  );
}
